import { ChangeDetectionStrategy, Component, DoCheck, Input } from '@angular/core';

export interface RenderedValue {
  x: number;
  y: number;
  value: number;
  viewValue: string;
}

@Component({
  selector: 'vi-graph',
  templateUrl: 'graph.component.html',
  styleUrls: ['graph.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViGraphComponent implements DoCheck {
  gridX: number[];
  gridY: number[];
  points: string;
  renderedValues: RenderedValue[] = [];
  readonly valueRadius: number = 16;

  @Input() data: { x: number; y: number }[] = [];
  @Input() minX: number;
  @Input() maxX: number;
  @Input() minY: number;
  @Input() maxY: number;
  @Input() gapX: number;
  @Input() gapY: number;
  @Input() labelX: string;
  @Input() labelY: string;
  @Input() maxFlowTemperature: number;
  @Input() graphWidth = 700;
  @Input() graphHeight = 300;
  @Input() unit = '';
  @Input() reverse: boolean;
  @Input() edgeValues: Boolean = false;
  @Input() roundingType: 'round' | 'floor' = 'round';
  @Input() converter: Function = (value: number) => value;
  @Input() isFlowTemperatureAvailable: boolean = false;

  ngDoCheck(): void {
    if (isNaN(this.minX) || isNaN(this.maxX) || isNaN(this.minY) || isNaN(this.maxY)) {
      throw Error('minX, minY, maxX, maxY must be a number');
    }

    if (this.minX >= this.maxX) {
      throw new Error('minX must be less then maxX');
    }

    if (this.minY >= this.maxY) {
      throw Error('minY must be less then maxY');
    }

    if (isNaN(this.gapX) || isNaN(this.gapY)) {
      throw Error('gapX and gapY must be a number');
    }

    if (this.gapX < 1 || this.gapY < 1) {
      throw Error('gapX and gapY must be grather then 0');
    }

    this.renderGrid();
    this.renderGraph();
    this.renderValues();
  }

  renderGrid(): void {
    this.gridX = [];
    this.gridY = [];

    for (let i = this.minX; i <= this.maxX; i += this.gapX) {
      this.gridX.push(this.transformX(i));
    }

    for (let j = this.minY; j <= this.maxY; j += this.gapY) {
      this.gridY.push(this.transformY(j));
    }
  }

  renderGraph(): void {
    const points: number[] = [];
    this.data.forEach((data) => {
      points.push(this.transformX(data.x));
      points.push(this.transformY(data.y));
    });
    this.points = points.toString();
  }

  renderValues(): void {
    if (this.data.length < 4) {
      return;
    }

    const data = this.data.slice();
    if (!this.edgeValues) {
      data[0].x = this.gapX + 1;
      data[this.data.length - 1].x = this.gapX + 1;
    }

    this.renderedValues = [];
    data.forEach((d) => {
      if (d.x % this.gapX === 0) {
        this.renderedValues.push({
          x: this.transformX(d.x),
          y: this.transformY(d.y),
          value: this.converter(d.y),
          viewValue: `${this.converter(Math[this.roundingType](d.y))}${this.unit}`,
        });
      }
    });
  }

  renderDottedRedLine(): string {
    // Get the leftmost and rightmost X coordinates
    const leftX = this.reverse ? this.transformX(this.minX) : this.transformX(this.maxX);
    const rightX = this.reverse ? this.transformX(this.maxX) : this.transformX(this.minX);
    const middleY = this.transformY(this.maxFlowTemperature);

    return `${leftX},${middleY} ${rightX},${middleY}`;
  }

  transformX(x: number): number {
    /**
     * IF REVERSED X
     *  maxX -> 0
     *  minX -> graphWidth
     *
     * ELSE
     *  minX -> 0
     *  maxX -> graphWidth
     */
    const min = this.reverse ? this.minX : this.maxX;
    const max = this.reverse ? this.maxX : this.minX;

    return (this.graphWidth * (max - x)) / (max - min);
  }

  transformY(y: number): number {
    /**
     * minY -> 0
     * maxY -> graphHeight
     */
    return -((this.graphHeight * (y - this.minY)) / (this.maxY - this.minY)) + this.graphHeight;
  }

  reverseFn(y: number): number {
    const min = this.reverse ? this.minX : this.maxX;
    const max = this.reverse ? this.maxX : this.minX;

    return this.converter(Math.round((y * (min - max)) / this.graphWidth + max));
  }

  isFirstX(id: number): boolean {
    return this.reverse ? id === this.gridX.length - 1 : id === 0;
  }

  isLastX(id: number): boolean {
    return this.reverse ? id === 0 : id === this.gridX.length - 1;
  }
}
